import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { useCategoryBreadcrumb } from "../../hooks/useCategories";
import { useQuery } from "@tanstack/react-query";
import { productService } from "../../services/firebaseConfig";

// Allow pages to provide context for dynamic segments like names for ids
declare global {
  interface Window {
    __breadcrumbContext?: {
      storeName?: string;
      productName?: string;
      categoryName?: string;
      categoryPath?: Array<{ id: string; name: string }>;
      orderNumber?: string;
    };
  }
}

interface BreadcrumbItem {
  label: string;
  href: string;
  isLast?: boolean;
  fullLabel?: string; // Store full label for title attribute when truncated
}

// Helper function to truncate product name with ellipsis
const truncateProductName = (name: string, maxWords: number = 5): string => {
  if (!name) return name;
  const words = name.trim().split(/\s+/);
  if (words.length <= maxWords) return name;
  return words.slice(0, maxWords).join(" ") + "...";
};

const Breadcrumb: React.FC = () => {
  const location = useLocation();
  const segments = location.pathname.split("/").filter(Boolean);
  const searchParams = new URLSearchParams(location.search);
  const categoryParam = searchParams.get("category");

  // Get product ID if on product detail page
  const productId =
    segments.length > 0 && segments[0] === "product" && segments[1]
      ? segments[1]
      : null;

  // Fetch product data to get category information (hooks must be called unconditionally)
  const { data: product } = useQuery({
    queryKey: ["product-for-breadcrumb", productId],
    queryFn: async () => {
      if (!productId) return null;
      return await productService.getProductById(productId);
    },
    enabled: !!productId,
    staleTime: 5 * 60 * 1000,
  });

  // Get primary category ID from product
  const primaryCategoryId = useMemo(() => {
    if (!product) return null;
    // Try categoryIds first (array of IDs)
    if (
      product.categoryIds &&
      Array.isArray(product.categoryIds) &&
      product.categoryIds.length > 0
    ) {
      return product.categoryIds[0];
    }
    // Fallback to categories array
    if (
      product.categories &&
      Array.isArray(product.categories) &&
      product.categories.length > 0
    ) {
      return product.categories[0];
    }
    return null;
  }, [product]);

  // Fetch category breadcrumb trail if we have a category ID
  const { data: categoryBreadcrumb = [] } = useCategoryBreadcrumb(
    primaryCategoryId || ""
  );

  // Helper function to get order label from context
  const getOrderLabel = (): string => {
    return window.__breadcrumbContext?.orderNumber
      ? `Order #${window.__breadcrumbContext.orderNumber}`
      : "Order Details";
  };

  // Build breadcrumb items with proper navigation paths
  const breadcrumbItems: BreadcrumbItem[] = useMemo(() => {
    // Early return for empty segments inside useMemo (not a hook)
    if (segments.length === 0) return [];
    const items: BreadcrumbItem[] = [];

    // Always start with Home
    items.push({ label: "Home", href: "/" });

    // Handle product detail pages with category path
    if (segments[0] === "product" && productId) {
      // Add Categories link
      items.push({ label: "Categories", href: "/categories" });

      // Add category path if available
      if (categoryBreadcrumb.length > 0) {
        categoryBreadcrumb.forEach((category) => {
          items.push({
            label: category.name,
            href: `/categories?category=${category.id}`,
          });
        });
      }

      // Use product name from context if available (no redundant "Product" label)
      const productName =
        window.__breadcrumbContext?.productName || product?.title || "Product";
      const truncatedProductName = truncateProductName(productName);
      items.push({
        label: truncatedProductName,
        fullLabel: productName, // Store full name for title attribute
        href: `/product/${productId}`,
        isLast: true,
      });

      return items;
    }

    // Handle categories page with category query parameter
    if (segments[0] === "categories" && categoryParam) {
      items.push({ label: "Categories", href: "/categories" });

      // Add category path from context if available
      if (
        window.__breadcrumbContext?.categoryPath &&
        window.__breadcrumbContext.categoryPath.length > 0
      ) {
        window.__breadcrumbContext.categoryPath.forEach(
          (category: { id: string; name: string }) => {
            items.push({
              label: category.name,
              href: `/categories?category=${encodeURIComponent(category.id)}`,
            });
          }
        );
      }

      // Add the selected category name
      const categoryName =
        window.__breadcrumbContext?.categoryName || categoryParam;
      items.push({
        label: categoryName,
        href: location.pathname + location.search,
        isLast: true,
      });
      return items;
    }

    // Handle store pages
    if (segments[0] === "store-page" && segments[1]) {
      items.push({ label: "Stores", href: "/stores" });
      const storeName = window.__breadcrumbContext?.storeName || "Store";
      items.push({ label: storeName, href: location.pathname, isLast: true });
      return items;
    }

    // Handle order detail pages
    if (segments[0] === "orders" && segments[1]) {
      items.push({ label: "Orders", href: "/orders" });
      items.push({
        label: getOrderLabel(),
        href: location.pathname,
        isLast: true,
      });
      return items;
    }

    // Handle order tracking with ID
    if (segments[0] === "order-tracking" && segments[1]) {
      items.push({ label: "Order Tracking", href: "/order-tracking" });
      items.push({
        label: getOrderLabel(),
        href: location.pathname,
        isLast: true,
      });
      return items;
    }

    // Handle order success with ID
    if (segments[0] === "order-success" && segments[1]) {
      items.push({
        label: "Order Success",
        href: location.pathname,
        isLast: true,
      });
      return items;
    }

    // Handle checkout result page
    if (segments[0] === "checkout" && segments[1] === "result") {
      items.push({ label: "Checkout", href: "/checkout" });
      items.push({
        label: "Payment Result",
        href: location.pathname,
        isLast: true,
      });
      return items;
    }

    // Handle payment routes with IDs
    if (segments[0] === "payment") {
      if (segments[1] === "success" && segments[2]) {
        items.push({
          label: "Payment Success",
          href: location.pathname,
          isLast: true,
        });
        return items;
      }
      if (segments[1] === "khalti") {
        if (segments[2] === "cancel") {
          items.push({ label: "Payment", href: "/payment" });
          items.push({ label: "Khalti Payment", href: "/payment/khalti" });
          items.push({
            label: "Cancelled",
            href: location.pathname,
            isLast: true,
          });
          return items;
        }
        if (segments[2]) {
          items.push({
            label: "Khalti Payment",
            href: location.pathname,
            isLast: true,
          });
          return items;
        }
      }
    }

    // Default: build breadcrumb from URL segments
    segments.forEach((seg, idx) => {
      const href = "/" + segments.slice(0, idx + 1).join("/");
      const isLast = idx === segments.length - 1;
      const raw = decodeURIComponent(seg);

      // Pretty label formatter
      let label = raw
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

      // Common route mappings
      const map: Record<string, string> = {
        stores: "Stores",
        "store-page": "Stores",
        products: "Products",
        product: "Product",
        categories: "Categories",
        cart: "Cart",
        checkout: "Checkout",
        wishlist: "Wishlist",
        "recently-viewed": "Recently Viewed",
        settings: "Settings",
        profile: "Profile",
        "edit-profile": "Edit Profile",
        account: "Account",
        addresses: "Addresses",
        privacy: "Privacy & Security",
        payments: "Payments & Wallet",
        payment: "Payment",
        orders: "Orders",
        "order-history": "Order History",
        "order-tracking": "Order Tracking",
        "order-success": "Order Success",
        loyalty: "Loyalty Coins",
        "loyalty-coins": "Loyalty Coins",
        "loyalty-points": "Loyalty Points",
        help: "Help & FAQ",
        about: "About",
        contact: "Contact",
        terms: "Terms & Conditions",
        shipping: "Shipping Information",
        returns: "Returns & Refunds",
        "gift-card": "Gift Card",
        "baakas-university": "Baakas University",
        wallet: "Wallet",
        vouchers: "Vouchers",
        following: "Following",
        "popular-products": "Popular Products",
        "featured-products": "Featured Products",
        "delete-account": "Delete Account",
        result: "Payment Result",
        khalti: "Khalti Payment",
        cancel: "Payment Cancelled",
        danger: "Danger Zone",
      };
      if (map[raw]) label = map[raw];

      // Use context for dynamic names
      if (
        segments[idx - 1] === "store-page" &&
        isLast &&
        window.__breadcrumbContext?.storeName
      ) {
        label = window.__breadcrumbContext.storeName;
      }

      items.push({ label, href, isLast });
    });

    return items;
  }, [
    segments,
    productId,
    product,
    categoryBreadcrumb,
    location.pathname,
    categoryParam,
  ]);

  // Early return after all hooks are called
  if (segments.length === 0 || breadcrumbItems.length === 0) return null;

  // Helper function to normalize href
  const normalizeHref = (href: string | undefined): string => {
    if (!href) return "#";
    return href === "/store-page" || href.startsWith("/store-page/")
      ? href.replace("/store-page", "/stores")
      : href;
  };

  // Helper component to render breadcrumb item
  const BreadcrumbItemRenderer = ({
    item,
    idx,
    isLast,
    isMobile = false,
    isEllipsis = false,
  }: {
    item: BreadcrumbItem | any;
    idx: number;
    isLast: boolean;
    isMobile?: boolean;
    isEllipsis?: boolean;
  }) => {
    const href = normalizeHref(item.href);

    if (isEllipsis) {
      return (
        <li className="flex-shrink-0">
          <span className="text-gray-400 px-1">...</span>
        </li>
      );
    }

    return (
      <React.Fragment>
        {idx > 0 && (
          <li className="flex-shrink-0">
            <ChevronRight
              className={isMobile ? "w-3 h-3" : "w-3.5 h-3.5 lg:w-4 lg:h-4"}
            />
          </li>
        )}
        <li className="flex-shrink-0">
          {idx === 0 ? (
            <Link
              to={item.href}
              className="flex items-center text-gray-500 hover:text-primary-500 transition-colors"
            >
              <Home
                className={isMobile ? "w-3.5 h-3.5" : "w-4 h-4 lg:w-5 lg:h-5"}
              />
              <span
                className={
                  isMobile
                    ? "ml-0.5 text-[10px] sm:text-xs"
                    : "ml-1 text-xs lg:text-sm"
                }
              >
                Home
              </span>
            </Link>
          ) : isLast ? (
            <span
              className={`text-gray-900 font-medium truncate ${
                isMobile
                  ? "max-w-[120px] sm:max-w-[180px]"
                  : "max-w-[300px] lg:max-w-none"
              }`}
              title={item.label}
            >
              {item.label}
            </span>
          ) : (
            <Link
              to={href}
              className={`text-gray-500 hover:text-primary-500 transition-colors truncate block ${
                isMobile
                  ? "max-w-[80px] sm:max-w-[120px]"
                  : "max-w-[150px] lg:max-w-none"
              }`}
              title={item.label}
            >
              {item.label}
            </Link>
          )}
        </li>
      </React.Fragment>
    );
  };

  return (
    <nav className="hidden md:block bg-white border-b border-gray-200 py-1 md:py-1.5 sticky top-[64px] sm:top-16 z-30">
      <div className="container-custom px-3 sm:px-4 md:px-6">
        <div className="overflow-x-auto scrollbar-hide">
          {/* Desktop: Show all items */}
          <ol className="flex items-center gap-1.5 lg:gap-2 text-sm lg:text-base min-w-max">
            {breadcrumbItems.map((item, idx) => {
              const isLast = item.isLast || idx === breadcrumbItems.length - 1;
              return (
                <BreadcrumbItemRenderer
                  key={`${item.href}-${idx}`}
                  item={item}
                  idx={idx}
                  isLast={isLast}
                  isMobile={false}
                />
              );
            })}
          </ol>
        </div>
      </div>
    </nav>
  );
};

export default Breadcrumb;
